import React from "react";
import { Box, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import {
  IoOpenOutline,
  IoPeopleOutline,
  IoMedkitOutline,
  IoPersonOutline,
  IoCalendarOutline,
} from "react-icons/io5";
import { addMonths, format, parseISO } from "date-fns";
import GlassCard from "components/GlassCard";

// ---------------------------------------------------------------------------
// Status configuration
// ---------------------------------------------------------------------------
const STATUS_CONFIG = {
  RECRUITING: { label: "Recruiting", color: "#10B981", bg: "rgba(16,185,129,0.12)" },
  ACTIVE: { label: "Active", color: "#3B82F6", bg: "rgba(59,130,246,0.12)" },
  NOT_YET_RECRUITING: { label: "Not Yet Recruiting", color: "#3B82F6", bg: "rgba(59,130,246,0.12)" },
  COMPLETED: { label: "Completed", color: "#6B7280", bg: "rgba(107,114,128,0.12)" },
};

const getStatusConfig = (status) =>
  STATUS_CONFIG[status] || { label: status, color: "#6B7280", bg: "rgba(107,114,128,0.12)" };

// ---------------------------------------------------------------------------
// Shared tooltip styling
// ---------------------------------------------------------------------------
const tooltipSx = {
  tooltip: {
    sx: {
      backgroundColor: "#1A1E3C",
      color: "rgba(255,255,255,0.9)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: "8px",
      fontSize: "0.75rem",
      lineHeight: 1.5,
      padding: "10px 14px",
      maxWidth: 360,
    },
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const truncate = (text, maxLen) => {
  if (!text) return "";
  return text.length > maxLen ? text.slice(0, maxLen).trim() + "\u2026" : text;
};

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    return parseISO(dateStr + "-01");
  }
  return parseISO(dateStr);
};

const getEstimatedRelease = (completionDate) => {
  const d = parseDate(completionDate);
  if (!d || isNaN(d.getTime())) return "TBD";
  return format(addMonths(d, 6), "MMM yyyy");
};

const getLeadInvestigator = (investigators) => {
  if (!investigators || investigators.length === 0) return "Not listed";
  const first = investigators[0];
  const namePart = first.split("\u2014")[0] || first.split("-")[0];
  return namePart.trim();
};

// ---------------------------------------------------------------------------
// AI Analysis label config
// ---------------------------------------------------------------------------
const AI_ANALYSIS_ITEMS = [
  { key: "marketing_goal", label: "Strategic Goal", accent: "#60A5FA" },
  { key: "competitive_implications", label: "Competitive Implications", accent: "#A78BFA" },
  { key: "study_weakness", label: "Study Weakness", accent: "#F59E0B" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const TrialCard = ({ trial }) => {
  const statusCfg = getStatusConfig(trial.status);
  const fullTitle = trial.official_title || "";

  return (
    <GlassCard sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        p={2.5}
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header: Title + Status Badge */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={1.5}
        >
          <Box flex={1} mr={1}>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.4)",
                fontSize: "0.7rem",
                letterSpacing: "0.05em",
                fontWeight: 600,
              }}
            >
              {trial.nct_id}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                color: "rgba(255,255,255,0.92)",
                fontWeight: 700,
                fontSize: "0.85rem",
                lineHeight: 1.4,
                mt: 0.3,
              }}
            >
              {fullTitle}
            </Typography>
          </Box>
          <Chip
            label={statusCfg.label}
            size="small"
            sx={{
              backgroundColor: statusCfg.bg,
              color: statusCfg.color,
              fontWeight: 700,
              fontSize: "0.65rem",
              height: "22px",
              border: `1px solid ${statusCfg.color}33`,
              flexShrink: 0,
              mt: 0.3,
            }}
          />
        </Box>

        {/* AI Analysis Section */}
        <Box mb={1.5}>
          {AI_ANALYSIS_ITEMS.map(({ key, label, accent }) => {
            const fullText = trial.ai_analysis?.[key] || "N/A";
            return (
              <Box
                key={key}
                mb={1}
                sx={{
                  borderLeft: `3px solid ${accent}66`,
                  pl: 1.5,
                  py: 0.3,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: accent,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 700,
                    fontSize: "0.58rem",
                    display: "block",
                    mb: 0.2,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "0.74rem",
                    lineHeight: 1.45,
                  }}
                >
                  {fullText}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Stats Row */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
            mt: "auto",
          }}
        >
          <StatItem
            icon={<IoPeopleOutline size={14} />}
            label="Enrollment"
            value={trial.enrollment?.toLocaleString() || "N/A"}
          />
          <StatItem
            icon={<IoMedkitOutline size={14} />}
            label="Condition"
            value={truncate(trial.conditions?.[0] || "N/A", 22)}
            fullText={trial.conditions?.join(", ")}
          />
          <StatItem
            icon={<IoPersonOutline size={14} />}
            label="Lead KOL"
            value={truncate(getLeadInvestigator(trial.investigators), 22)}
            fullText={trial.investigators?.[0] || "Not listed"}
          />
        </Box>

        {/* Footer: Estimated Release + Link */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pt={1.5}
          sx={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <IoCalendarOutline size={13} color="rgba(255,255,255,0.4)" />
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.4)",
                fontSize: "0.68rem",
                fontWeight: 500,
              }}
            >
              Est. Market Release:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: "0.68rem",
                fontWeight: 700,
              }}
            >
              {getEstimatedRelease(trial.completion_date)}
            </Typography>
          </Box>
          {trial.study_url && (
            <IconButton
              size="small"
              href={trial.study_url}
              target="_blank"
              rel="noopener noreferrer"
              component="a"
              sx={{
                color: "rgba(255,255,255,0.35)",
                "&:hover": { color: "#60A5FA" },
                p: 0.5,
              }}
            >
              <IoOpenOutline size={15} />
            </IconButton>
          )}
        </Box>
      </Box>
    </GlassCard>
  );
};

// ---------------------------------------------------------------------------
// Sub-component: Stat item with optional tooltip
// ---------------------------------------------------------------------------
const StatItem = ({ icon, label, value, fullText }) => {
  const content = (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Box sx={{ color: "rgba(255,255,255,0.35)", display: "flex" }}>{icon}</Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.35)",
            fontSize: "0.58rem",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 600,
            display: "block",
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.8)",
            fontSize: "0.72rem",
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );

  if (fullText && fullText !== value) {
    return (
      <Tooltip title={fullText} placement="top" arrow componentsProps={tooltipSx}>
        <Box sx={{ cursor: "default" }}>{content}</Box>
      </Tooltip>
    );
  }

  return content;
};

export default TrialCard;
